import { HallOfFameSection } from "@app/certificates/components/HallOfFameSection";
import { LeagueHomeCard } from "@app/league/components/LeagueHomeCard";
import { useModernHomePage } from "@app/home/hooks/use-modern-home-page.hook";
import { ShortsRailContainer } from "@app/shorts/components/ShortsRail/ShortsRailContainer";
import type { GuestHomeResponse, UserHomeResponse } from "@core/home/types";
import type { LeagueSummary } from "@core/league/types";
import { TrainingGroundHomeCard } from "../components/TrainingGroundHomeCard";
import { ContinueWatchingSection } from "../components/modern-home/ContinueWatchingSection";
import { ModernHomeAnnouncements } from "../components/modern-home/ModernHomeAnnouncements";
import { ModernHomeAssessments } from "../components/modern-home/ModernHomeAssessments";
import { ModernHomeCampaigns } from "../components/modern-home/ModernHomeCampaigns";
import { ModernHomeCategories } from "../components/modern-home/ModernHomeCategories";
import { ModernHomeGuestGrades } from "../components/modern-home/ModernHomeGuestGrades";
import { ModernHomeHero } from "../components/modern-home/ModernHomeHero";
import { ModernHomeLatestLessons } from "../components/modern-home/ModernHomeLatestLessons";
import { ModernHomeLessonSpotlight } from "../components/modern-home/ModernHomeLessonSpotlight";
import type { TrainingGroundHomeData } from "../utils/training-ground-home";

type ModernHomePageContentProps = {
  data: UserHomeResponse | GuestHomeResponse;
  trainingGroundHome?: TrainingGroundHomeData;
  leagueHome?: LeagueSummary;
};

export function ModernHomePageContent({
  data,
  trainingGroundHome,
  leagueHome,
}: ModernHomePageContentProps) {
  const home = useModernHomePage(data);
  const hasCategories = home.categories.length > 0;
  const hasAssessments = home.assessments.length > 0;
  const hasDiscoveryContent = hasCategories || hasAssessments;

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.1),_transparent_32rem),radial-gradient(circle_at_20%_35%,_rgba(34,211,238,0.08),_transparent_28rem),linear-gradient(to_bottom,_#f8fafc,_#ffffff)] pb-24 pt-6 dark:bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.16),_transparent_32rem),radial-gradient(circle_at_20%_35%,_rgba(34,211,238,0.09),_transparent_28rem),linear-gradient(to_bottom,_#0f172a,_#020617)]">
      <div className="container mx-auto max-w-[90rem] space-y-10 px-4 sm:px-6 lg:px-8">
        <div className={`grid gap-5 ${home.topGridClassName}`}>
          {home.isGuest ? (
            <ModernHomeHero hero={home.hero} isGuest={home.isGuest} />
          ) : null}
          <ModernHomeAnnouncements announcements={home.announcements} />
        </div>

        {!home.isGuest && <LeagueHomeCard data={leagueHome} />}

        {!home.isGuest && (
          <TrainingGroundHomeCard data={trainingGroundHome} />
        )}

        <ModernHomeCampaigns banners={home.banners} sliders={home.sliders} />
        <ShortsRailContainer shorts={home.shorts} limit={12} />
        <HallOfFameSection />
        <ContinueWatchingSection entries={home.continueWatching} />
        <ModernHomeLatestLessons lessons={home.latestLessons} />
        <ModernHomeLessonSpotlight lessons={home.pinnedLessons} />

        {hasDiscoveryContent && (
          <div
            className={
              hasCategories && hasAssessments
                ? "grid items-start gap-5 lg:grid-cols-[minmax(18rem,0.72fr)_minmax(0,1.5fr)]"
                : "grid grid-cols-1"
            }
          >
            <ModernHomeCategories
              categories={home.categories}
              compact={hasAssessments}
            />
            <ModernHomeAssessments assessments={home.assessments} />
          </div>
        )}

        <ModernHomeGuestGrades grades={home.grades} />
      </div>
    </main>
  );
}
